/**
 * «Задать вопрос» (client-questions; ТЗ 26.10.2026, п.9.7: «задать вопрос можно как из раздела „Мои
 * вопросы“, так и из заказа»): окно с темой, текстом и вложениями. Из заказа вопрос привязан к нему
 * — его получит ответственный за заказ (ответ В29). После отправки — страница вопроса.
 */
import { useState, type FormEvent } from 'react'
import { MessageCircleQuestion, X } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { questionsApi, type OrderDataStatus } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { cn } from '@/lib/utils'
import { Dialog, DialogClose, DialogContent } from '@/design-system/components/primitives/dialog'
import { Card } from '@/design-system/components/layout/card'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  AttachmentsInput,
  QUESTION_BODY_MAX,
  QUESTION_SUBJECT_MAX,
  TEXTAREA_CLASS,
  attachmentsError,
} from './question-ui'

export function AskQuestionDialog({
  open,
  onClose,
  order,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  /** Заказ, из которого задан вопрос. */
  order?: { id: string; number: string }
  /** Вопрос создан (обновить счётчики кабинета, список). */
  onCreated?: () => void
}) {
  const navigate = useNavigate()
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<{ subject?: string; body?: string }>({})
  const filesError = attachmentsError(files)

  function reset() {
    setSubject('')
    setBody('')
    setFiles([])
    setError(null)
    setFieldErrors({})
  }

  function close() {
    if (busy) return
    reset()
    onClose()
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    const errors: { subject?: string; body?: string } = {}
    if (!subject.trim()) errors.subject = 'Укажите тему вопроса'
    if (!body.trim()) errors.body = 'Напишите вопрос'
    setFieldErrors(errors)
    if (errors.subject || errors.body || filesError) return
    setBusy(true)
    setError(null)
    try {
      const created = await questionsApi.create({ subject, body, orderId: order?.id, files })
      reset()
      onCreated?.()
      onClose()
      navigate(`/questions/${created.id}`)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось отправить вопрос'))
    } finally {
      setBusy(false)
    }
  }

  const title = order ? `Вопрос по заказу № ${order.number}` : 'Задать вопрос'
  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : close())}>
      <DialogContent title={title} description="Вопрос получит менеджер Центрики" className="max-w-narrow">
        <Card
          header={
            <div className="flex items-center justify-between gap-3">
              <span>{title}</span>
              <DialogClose asChild>
                <button
                  type="button"
                  aria-label="Закрыть"
                  disabled={busy}
                  className={cn(
                    '-mr-1 rounded-8 p-1 text-txt-secondary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                    busy ? 'pointer-events-none opacity-50' : 'hover:bg-hover hover:text-txt',
                  )}
                >
                  <X className="size-4 stroke-current" aria-hidden />
                </button>
              </DialogClose>
            </div>
          }
        >
          <form onSubmit={submit} noValidate>
            <p className="mb-4 text-txt-secondary">
              Вопрос получит менеджер Центрики, ответ придёт в «Мои вопросы» и на почту.
            </p>
            {error ? (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            ) : null}
            <Field label="Тема" required error={fieldErrors.subject}>
              {(p) => (
                <Input
                  {...p}
                  value={subject}
                  maxLength={QUESTION_SUBJECT_MAX}
                  disabled={busy}
                  onChange={(e) => setSubject(e.target.value)}
                />
              )}
            </Field>
            <Field label="Вопрос" required error={fieldErrors.body}>
              {(p) => (
                <textarea
                  {...p}
                  className={TEXTAREA_CLASS}
                  value={body}
                  maxLength={QUESTION_BODY_MAX}
                  rows={5}
                  disabled={busy}
                  onChange={(e) => setBody(e.target.value)}
                />
              )}
            </Field>
            <AttachmentsInput files={files} onChange={setFiles} disabled={busy} error={filesError} />
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              <Button type="submit" size="sm" disabled={busy || Boolean(filesError)}>
                {busy ? 'Отправка…' : 'Отправить вопрос'}
              </Button>
              <Button type="button" size="sm" variant="secondary" disabled={busy} onClick={close}>
                Отмена
              </Button>
            </div>
          </form>
        </Card>
      </DialogContent>
    </Dialog>
  )
}

/**
 * «Задать вопрос» на странице заказа и в его анкете: у поданного заказа (черновик Центрика ещё не
 * видит — из него вопрос задают в разделе «Мои вопросы»).
 */
export function OrderQuestionButton({
  order,
  onCreated,
}: {
  order: { id: string; number: string; dataStatus: OrderDataStatus }
  onCreated?: () => void
}) {
  const [open, setOpen] = useState(false)
  if (order.dataStatus === 'draft') return null
  return (
    <>
      <Button
        variant="secondary"
        leftIcon={<MessageCircleQuestion className="size-4 stroke-current" aria-hidden />}
        onClick={() => setOpen(true)}
      >
        Задать вопрос
      </Button>
      <AskQuestionDialog
        open={open}
        onClose={() => setOpen(false)}
        order={{ id: order.id, number: order.number }}
        onCreated={onCreated}
      />
    </>
  )
}
